import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { endpoints } from '@/api/endpoints';
import { LoadingState, ErrorState, EmptyState } from '@/components/states';
import type { ProjectSummary } from '@/types/api';

function formatDate(iso: string, locale: string) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ProjectRow({ p }: { p: ProjectSummary }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'es';

  return (
    <tr className="border-b border-border hover:bg-surface-3/50 transition-colors group">
      <td className="px-4 py-3 font-mono text-xs text-ink-4">#{p.id}</td>
      <td className="px-4 py-3 text-ink">
        {p.project_name ?? <span className="text-ink-4 italic">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-ink-3">
        {p.address ?? <span className="text-ink-4">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-ink-3">
        {p.designer ?? <span className="text-ink-4">—</span>}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink-4 text-right">
        {p.item_count}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink-3">
        <div>{formatDate(p.created_at, locale)}</div>
        {p.date && p.date !== p.created_at && (
          <div className="mt-1 text-[11px] text-ink-4">{p.date}</div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/projects/${p.id}`}
          className="inline-block text-xs font-medium px-3 py-1.5 rounded
                     border border-border-2 text-ink-3
                     group-hover:border-accent group-hover:text-accent
                     transition-colors"
        >
          {t('dashboard.open')} →
        </Link>
      </td>
    </tr>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['projects'],
    queryFn: () => endpoints.listProjects(),
  });

  return (
    <div className="animate-fade-up">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-sans text-xl font-semibold tracking-[-0.015em] mb-1">
            {t('dashboard.title')}
          </h1>
          <p className="text-sm text-ink-3">{t('dashboard.subtitle')}</p>
        </div>
        <a
          href={import.meta.env.VITE_PLANSPEC_UI_URL ?? 'http://localhost:8501'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium px-4 py-2.5 rounded
                     bg-accent/15 text-accent border border-accent/40
                     hover:bg-accent/25 hover:border-accent transition-all
                     flex items-center gap-2 flex-shrink-0"
          title={t('dashboard.upload_hint')}
        >
          <span className="text-base leading-none">↗</span>
          {t('dashboard.upload')}
        </a>
      </div>

      {query.isLoading && <LoadingState />}

      {query.isError && (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState
          title={t('dashboard.empty_title')}
          body={t('dashboard.empty_body')}
        />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden bg-surface-2">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[880px]">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="px-4 py-3 text-left text-[11px] font-medium text-ink-4">
                  {t('dashboard.col_id')}
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-ink-4">
                  {t('dashboard.col_name')}
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-ink-4">
                  {t('dashboard.col_address')}
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-ink-4">
                  {t('dashboard.col_designer')}
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-medium text-ink-4">
                  {t('dashboard.col_items')}
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-medium text-ink-4">
                  {t('dashboard.col_created')}
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {query.data.map((p) => <ProjectRow key={p.id} p={p} />)}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
